import './RoutePathInfo.scss'

import { HDSIcon } from '@here/hds-react-components'
import { RouteListItemSection, RouteListItemTransportMode } from 'components/RouteListItem'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import { i18n } from 'lib/i18n'
import React from 'react'
import { capitalize } from 'utils/formatters'

import { Section } from '../Rdc'
import { RdcSectionTransportMode } from '../RdcSection/RdcSection'
import TransportModeFlag from '../TransportModeFlag'

interface RoutePathInfoProps {
  sections: RouteListItemSection[]
  transportMode: TransportModesPanelMode
}

export default function RoutePathInfo({
  sections,
  transportMode,
}: RoutePathInfoProps): JSX.Element {
  const mergedSecctions =
    transportMode === TransportModesPanelMode.PublicTransport
      ? sections
      : mergeSameRouteSections(sections)

  return (
    <>
      {mergedSecctions.map((section, index) => (
        <React.Fragment key={index}>
          {index > 0 && <HDSIcon name="chevron-right" size="8px" className="RoutePath__chevron" />}

          {isPrimaryMode(transportMode, section.mode) ? (
            <TransportModeFlag
              key={section.title}
              color={section.colors.text}
              bgColor={section.colors.chip}
              icon={section.mode}
            >
              {section.title ?? getTransportModeTranslation(section.mode)}
            </TransportModeFlag>
          ) : (
            <div className="RoutePath__support">
              <HDSIcon name={section.mode} category="travel-transport-tracking" />
              <span className="RoutePath__support-title">
                {section.duration < 60 ? `< 1'` : `${Math.floor(section.duration / 60)}'`}
              </span>
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  )
}

function isPrimaryMode(
  transportMode: TransportModesPanelMode,
  sectionMode: RouteListItemTransportMode
): boolean {
  return (
    (transportMode === TransportModesPanelMode.Car &&
      sectionMode === RouteListItemTransportMode.Car) ||
    (transportMode === TransportModesPanelMode.PublicTransport &&
      sectionMode !== RouteListItemTransportMode.Car &&
      sectionMode !== RouteListItemTransportMode.Bicycle &&
      sectionMode !== RouteListItemTransportMode.Pedestrian) ||
    (transportMode === TransportModesPanelMode.Bike &&
      sectionMode === RouteListItemTransportMode.Bicycle) ||
    (transportMode === TransportModesPanelMode.Walk &&
      sectionMode === RouteListItemTransportMode.Pedestrian)
  )
}

function mergeSameRouteSections(sections: RouteListItemSection[]): RouteListItemSection[] {
  const newSections: RouteListItemSection[] = []
  sections.forEach((section) => {
    if (newSections.length === 0) {
      newSections.push(section)
      return
    }
    const prevSection = newSections[newSections.length - 1]
    if (section.mode !== prevSection.mode) {
      newSections.push(section)
      return
    }

    const mergedSection: RouteListItemSection = {
      ...prevSection,
      delay: (prevSection.delay ?? 0) + (section.delay ?? 0),
      duration: (prevSection.duration ?? 0) + (section.duration ?? 0),
    }

    newSections.pop()
    newSections.push(mergedSection)
  })
  return newSections
}

export function fromRdcSectionToRouteListItemSection(section: Section): RouteListItemSection {
  return {
    colors: {
      chip: section.color.background,
      icon: section.color.text,
      text: section.color.text,
    },
    delay: section.delay,
    duration: section.duration,
    mode: fromRdcSectionTransportModeToRouteListItemTransportMode(section.mode),
    title: section.name ?? getRdcSectionTransportModeTranslation(section.mode),
  }
}

function fromRdcSectionTransportModeToRouteListItemTransportMode(
  mode: RdcSectionTransportMode
): RouteListItemTransportMode {
  switch (mode) {
    case RdcSectionTransportMode.Car:
    case RdcSectionTransportMode.Drive:
    case RdcSectionTransportMode.ParkedCar:
      return RouteListItemTransportMode.Car
    case RdcSectionTransportMode.Train:
      return RouteListItemTransportMode.Train
    case RdcSectionTransportMode.Tram:
      return RouteListItemTransportMode.Tram
    case RdcSectionTransportMode.Underground:
      return RouteListItemTransportMode.Subway
    case RdcSectionTransportMode.Bike:
      return RouteListItemTransportMode.Bicycle
    case RdcSectionTransportMode.Bus:
      return RouteListItemTransportMode.Bus
    case RdcSectionTransportMode.Ferry:
      return RouteListItemTransportMode.Ferry
    case RdcSectionTransportMode.Pedestrian:
      return RouteListItemTransportMode.Pedestrian
    default:
      return RouteListItemTransportMode.Unknown
  }
}

export function getRdcSectionTransportModeTranslation(
  rdcSectionTransportMode: RdcSectionTransportMode
): string {
  return getTransportModeTranslation(
    fromRdcSectionTransportModeToRouteListItemTransportMode(rdcSectionTransportMode)
  )
}

export function getTransportModeTranslation(transportMode: RouteListItemTransportMode): string {
  switch (transportMode) {
    case RouteListItemTransportMode.Bus:
      return capitalize(i18n('route.transport.mode.bus'))
    case RouteListItemTransportMode.Car:
      return capitalize(i18n('route.transport.mode.car'))
    case RouteListItemTransportMode.Pedestrian:
      return capitalize(i18n('route.transport.mode.walk'))
    case RouteListItemTransportMode.Bicycle:
      return capitalize(i18n('route.transport.mode.bike'))
    case RouteListItemTransportMode.Subway:
      return capitalize(i18n('route.transport.mode.subway'))
    case RouteListItemTransportMode.Train:
      return capitalize(i18n('route.transport.mode.train'))
    case RouteListItemTransportMode.Tram:
      return capitalize(i18n('route.transport.mode.tram'))
    case RouteListItemTransportMode.Ferry:
      return capitalize(i18n('route.transport.mode.ferry'))
    default:
      return ''
  }
}
